import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/brand";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      <div className="lg:w-1/2 bg-navy text-white flex-col justify-between p-6 sm:p-10 lg:p-12 relative overflow-hidden flex">
        <img src={LOGO_URL} alt="Moaglee Commercial Real Estate"
             className="max-h-24 sm:max-h-32 lg:max-h-40 w-auto self-start" data-testid="login-brand-logo" />

        <div className="relative z-10 max-w-md mt-8 lg:mt-0">
          <div className="kicker text-brass mb-4">The Broker&apos;s Offer Desk</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display leading-[1.05]">
            Draft a defensible <span className="text-brass italic">LOI</span> in minutes, not hours.
          </h1>
          <p className="text-white/70 mt-4 sm:mt-6 leading-relaxed text-sm">
            Uploaded documents, cited public records, verified financials, and a review-ready
            document package. Built for commercial brokers who protect their commission.
          </p>
          <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-3 sm:gap-4">
            {[
              { k: "Sources", v: "Cited" },
              { k: "Facts", v: "Verified" },
              { k: "Docs", v: "Branded" },
            ].map((s) => (
              <div key={s.k} className="border-l border-brass/60 pl-3">
                <div className="text-[10px] uppercase tracking-widest text-white/50">{s.k}</div>
                <div className="font-mono text-base sm:text-lg text-brass">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-8 lg:mt-0">
          © Moaglee Commercial Real Estate — All information subject to broker, client, title, lender, and legal review.
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="kicker mb-3">Sign In</div>
          <h2 className="text-2xl sm:text-3xl font-display mb-6 sm:mb-8">Continue to your offer desk</h2>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass"
                placeholder="broker@example.com"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass"
                placeholder="••••••••"
              />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="btn-brass w-full justify-center py-3 disabled:opacity-60">
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="mt-8 text-sm text-slate-600">
            No account yet?{" "}
            <Link to="/register" className="link-brass font-medium" data-testid="link-to-register">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
